/**
 * BackupPanel — admin UI for /api/admin/backup.
 * Shows scheduler status, latest checkpoint, catalog with type badges + actions.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Card } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Alert, AlertDescription } from "../ui/alert";
import { toast } from "sonner";
import { RefreshCw, Save, ShieldCheck, PlayCircle, Bell, Download, FileText, CloudUpload, CloudOff, CheckCircle2, Cloud } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND}/api/admin/backup`;

function authHeaders() {
  const token = localStorage.getItem("access_token") || localStorage.getItem("token") || "";
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function fmtBytes(n) {
  if (!n && n !== 0) return "—";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} kB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}

function fmtDate(iso) {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString("fr-FR"); } catch { return iso; }
}

function ShortHash({ value }) {
  if (!value) return <span className="text-muted-foreground">—</span>;
  return <code className="text-xs">{value.slice(0, 16)}…</code>;
}

function TypeBadge({ type }) {
  if (type === "scheduled") return <Badge className="bg-blue-100 text-blue-800">planifié</Badge>;
  return <Badge className="bg-slate-100 text-slate-700">manuel</Badge>;
}

export default function BackupPanel() {
  const [status, setStatus] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [remoteList, setRemoteList] = useState([]);
  const [syncing, setSyncing] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [restoreResult, setRestoreResult] = useState(null);
  const [validateResult, setValidateResult] = useState(null);
  const [sortOrder, setSortOrder] = useState("newest");
  const [filterType, setFilterType] = useState("all");
  const [filterValid, setFilterValid] = useState("all");
  const [expanded, setExpanded] = useState({});

  // ---- pCloud helpers ------------------------------------------------------
  const pcloudStatus = status?.providers?.find((p) => p.provider === "pcloud") || null;
  const pcloudEnabled = !!pcloudStatus?.enabled;
  const remoteIdSet = useMemo(() => new Set((remoteList || []).map((r) => r.checkpoint_id)), [remoteList]);
  const lastVerifiedId = pcloudStatus?.last_verified_checkpoint;

  // States: "not_synced" | "syncing" | "synced" | "verified" | "failed"
  const remoteStateFor = (id) => {
    if (syncing[id] === "in_progress") return "syncing";
    if (syncing[id] === "failed") return "failed";
    if (remoteIdSet.has(id)) return id === lastVerifiedId ? "verified" : "synced";
    return "not_synced";
  };

  const RemoteBadge = ({ id }) => {
    const s = remoteStateFor(id);
    const map = {
      not_synced: { cls: "bg-slate-100 text-slate-700", label: "Non synchronisé", Icon: CloudOff },
      syncing:    { cls: "bg-blue-100 text-blue-800",    label: "Synchronisation en cours", Icon: CloudUpload },
      synced:     { cls: "bg-emerald-100 text-emerald-800", label: "Synchronisé", Icon: Cloud },
      verified:   { cls: "bg-green-100 text-green-800", label: "Vérifié", Icon: CheckCircle2 },
      failed:     { cls: "bg-red-100 text-red-800",     label: "Échec", Icon: CloudOff },
    }[s];
    const Icon = map.Icon;
    return (
      <Badge className={`${map.cls} flex items-center gap-1`} data-testid={`pcloud-badge-${id}`}>
        <Icon className="h-3 w-3" /> {map.label}
      </Badge>
    );
  };

  const syncToPcloud = async (id, cp) => {
    // Client-side pre-check that mirrors the 409 server refusal for
    // unvalidated / non-restore-tested checkpoints.
    if (!cp?.last_restore_test?.ok) {
      toast.error("Refusé : checkpoint non validé ou non restore-testé.");
      return;
    }
    setSyncing((s) => ({ ...s, [id]: "in_progress" }));
    try {
      await axios.post(`${API}/pcloud/upload/${id}`, {}, { headers: authHeaders(), timeout: 600000 });
      // Server-side verify right after upload (best-effort — non-blocking on success message)
      try {
        await axios.post(`${API}/pcloud/verify/${id}`, {}, { headers: authHeaders(), timeout: 180000 });
      } catch (_ve) { /* verify failure surfaced by next reload */ }
      toast.success(`Sync pCloud OK : ${id}`);
      setSyncing((s) => ({ ...s, [id]: null }));
      await load();
    } catch (e) {
      const code = e?.response?.data?.detail;
      const msg = {
        checkpoint_not_restore_tested: "Refusé : checkpoint non validé ou non restore-testé.",
        pcloud_disabled_or_unauthenticated: "Refusé : pCloud désactivé ou non authentifié.",
        checkpoint_not_found: "Refusé : checkpoint introuvable.",
      }[code] || e?.response?.data?.detail || e?.message || "Échec synchro pCloud";
      toast.error(msg);
      setSyncing((s) => ({ ...s, [id]: "failed" }));
    }
  };

  const downloadCheckpoint = async (id) => {
    try {
      const r = await axios.get(`${API}/checkpoints/${id}/download`, {
        headers: authHeaders(), responseType: "blob", timeout: 300000,
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `itercatus_checkpoint_${id}.tar.gz`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Téléchargement lancé : ${id}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Échec téléchargement");
    }
  };

  const validateCheckpoint = async (id) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/validate/${id}`, {}, { headers: authHeaders(), timeout: 60000 });
      if (r.data.ok) toast.success(`Checkpoint OK — pas de .env détecté.`);
      else toast.error(`Validation KO — ${r.data.error || "voir manifeste"}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Échec validation");
    } finally { setBusy(false); }
  };

  const restoreTestCheckpoint = async (id) => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/restore-test/${id}`, {}, { headers: authHeaders(), timeout: 600000 });
      if (r.data.ok) toast.success("Restore-test OK — base temporaire supprimée.");
      else toast.error("Restore-test KO : " + (r.data.error || "counts mismatch"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Échec restore-test");
    } finally { setBusy(false); }
  };

  const filteredCatalog = useMemo(() => {
    const list = (catalog || []).slice();
    list.sort((a, b) => {
      const av = a.created_at || "";
      const bv = b.created_at || "";
      return sortOrder === "newest" ? bv.localeCompare(av) : av.localeCompare(bv);
    });
    return list.filter((c) => {
      if (filterType !== "all" && (c.checkpoint_type || "manual") !== filterType) return false;
      if (filterValid === "valid" && !(c.last_restore_test && c.last_restore_test.ok)) return false;
      if (filterValid === "invalid" && (c.last_restore_test && c.last_restore_test.ok)) return false;
      return true;
    });
  }, [catalog, sortOrder, filterType, filterValid]);

  const load = useCallback(async () => {
    try {
      const [s, c, rl] = await Promise.all([
        axios.get(`${API}/status`, { headers: authHeaders(), timeout: 8000 }),
        axios.get(`${API}/checkpoints`, { headers: authHeaders(), timeout: 8000 }),
        axios.get(`${API}/pcloud/checkpoints`, { headers: authHeaders(), timeout: 8000 }).catch(() => ({ data: { checkpoints: [] } })),
      ]);
      setStatus(s.data);
      setCatalog(c.data?.checkpoints || []);
      setRemoteList(rl.data?.checkpoints || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Erreur inconnue");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createCheckpoint = async () => {
    setBusy(true);
    setValidateResult(null);
    setRestoreResult(null);
    try {
      const r = await axios.post(`${API}/checkpoint`, {}, { headers: authHeaders(), timeout: 300000 });
      toast.success(`Sauvegarde créée : ${r.data.checkpoint_id}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Échec sauvegarde");
    } finally {
      setBusy(false);
    }
  };

  const runValidate = async () => {
    const id = status?.latest_checkpoint?.checkpoint_id;
    if (!id) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/validate/${id}`, {}, { headers: authHeaders(), timeout: 60000 });
      setValidateResult(r.data);
      if (r.data.ok) toast.success("Checksums vérifiés — aucun .env détecté.");
      else toast.error("Validation KO");
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Échec validation");
    } finally {
      setBusy(false);
    }
  };

  const runRestoreTest = async () => {
    const id = status?.latest_checkpoint?.checkpoint_id;
    if (!id) return;
    setBusy(true);
    try {
      const r = await axios.post(`${API}/restore-test/${id}`, {}, { headers: authHeaders(), timeout: 600000 });
      setRestoreResult(r.data);
      if (r.data.ok) toast.success("Restore-test OK — base temporaire supprimée.");
      else toast.error("Restore-test KO : " + (r.data.error || "counts mismatch"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Échec restore-test");
    } finally {
      setBusy(false);
    }
  };

  const runSchedulerNow = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/scheduler/run-now`, {}, { headers: authHeaders(), timeout: 300000 });
      if (r.data?.ok) toast.success("Cycle planificateur exécuté avec succès.");
      else toast.error("Cycle planificateur KO : " + (r.data?.error || "voir alertes"));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Échec cycle");
    } finally { setBusy(false); }
  };

  const ackAlerts = async () => {
    try {
      await axios.post(`${API}/alerts/ack`, {}, { headers: authHeaders(), timeout: 8000 });
      toast.success("Alertes acquittées.");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e?.message || "Échec acquittement");
    }
  };

  const latest = status?.latest_checkpoint;
  const providers = status?.providers || [];
  const sch = status?.scheduler;
  const openAlerts = (sch?.alerts || []).filter((a) => !a.acknowledged);

  return (
    <Card className="p-6 space-y-4" data-testid="backup-panel">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-heading text-lg font-semibold text-primary">Sauvegardes locales</h3>
          <p className="text-xs text-muted-foreground">
            Racine : <code>{status?.checkpoints_root || "—"}</code>
          </p>
        </div>
        <div className="flex gap-2 flex-wrap justify-end">
          <Button size="sm" variant="outline" onClick={load} data-testid="backup-refresh-btn">
            <RefreshCw className="h-3 w-3 mr-1" /> Rafraîchir
          </Button>
          <Button size="sm" variant="outline" onClick={runSchedulerNow} disabled={busy} data-testid="scheduler-run-now-btn">
            <PlayCircle className="h-3 w-3 mr-1" /> Cycle planificateur
          </Button>
          <Button size="sm" onClick={createCheckpoint} disabled={busy} data-testid="backup-create-btn">
            <Save className="h-3 w-3 mr-1" /> Créer une sauvegarde
          </Button>
        </div>
      </div>

      {sch && (
        <div className="rounded-lg border bg-muted/30 p-3 text-xs" data-testid="scheduler-status">
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            <span>Planificateur : <strong>{sch.enabled ? "activé" : "désactivé"}</strong></span>
            <span>Horaire : {String(sch.schedule?.hour_utc).padStart(2, "0")}:{String(sch.schedule?.minute_utc).padStart(2, "0")} UTC</span>
            <span>Rétention : {sch.retention?.daily} q / {sch.retention?.weekly} h / {sch.retention?.monthly} m</span>
            <span>Prochaine exécution : {fmtDate(sch.next_run_at)}</span>
            <span>Dernier succès : {fmtDate(sch.last_success_at)}</span>
          </div>
          {sch.last_error && (
            <div className="text-red-700 mt-1">Dernière erreur : {sch.last_error}</div>
          )}
        </div>
      )}

      {openAlerts.length > 0 && (
        <Alert variant="destructive" data-testid="scheduler-alerts">
          <Bell className="h-4 w-4" />
          <AlertDescription>
            <div className="flex items-center justify-between gap-3">
              <div>{openAlerts.length} alerte(s) sauvegarde non acquittée(s).</div>
              <Button size="sm" variant="outline" onClick={ackAlerts} data-testid="ack-alerts-btn">Acquitter</Button>
            </div>
            <ul className="mt-2 space-y-1 text-xs">
              {openAlerts.slice(0, 5).map((a, i) => (
                <li key={i}><strong>[{a.level}]</strong> {fmtDate(a.ts)} — {a.message}</li>
              ))}
            </ul>
          </AlertDescription>
        </Alert>
      )}

      {error && <Alert variant="destructive"><AlertDescription>{error}</AlertDescription></Alert>}

      {latest ? (
        <div className="rounded-lg border bg-muted/30 p-4 space-y-2" data-testid="backup-latest">
          <div className="flex items-center gap-2 flex-wrap text-sm">
            <Badge className="bg-green-100 text-green-800">Dernier</Badge>
            <TypeBadge type={latest.checkpoint_type} />
            <span className="font-mono text-xs">{latest.checkpoint_id}</span>
            <span className="text-xs text-muted-foreground">
              · {fmtDate(latest.created_at)} · durée {Math.round(latest.duration_seconds)}s
            </span>
            {latest.recovery?.recovery_ready && (
              <Badge className="bg-emerald-100 text-emerald-800">DR-ready v{latest.recovery.dr_format_version}</Badge>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><span className="text-muted-foreground">Collections</span><br /><span className="font-medium">{latest.database?.collections}</span></div>
            <div><span className="text-muted-foreground">Documents</span><br /><span className="font-medium">{latest.database?.total_documents}</span></div>
            <div><span className="text-muted-foreground">Users</span><br /><span className="font-medium">{latest.database?.users_count}</span></div>
            <div><span className="text-muted-foreground">pre_launch_mode</span><br /><span className="font-medium">{String(latest.database?.pre_launch_mode)}</span></div>
          </div>
          <div className="space-y-1 text-xs pt-2 border-t">
            {(latest.files || []).slice(0, 4).map((f) => (
              <div key={f.relpath} className="flex flex-wrap items-center gap-2">
                <code>{f.relpath}</code>
                <span className="text-muted-foreground">{fmtBytes(f.bytes)}</span>
                <ShortHash value={f.sha256} />
              </div>
            ))}
            {(latest.files || []).length > 4 && (
              <div className="text-muted-foreground">+ {latest.files.length - 4} fichier(s) supplémentaire(s)…</div>
            )}
          </div>
          <div className="flex gap-2 pt-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={runValidate} disabled={busy} data-testid="backup-validate-btn">
              <ShieldCheck className="h-3 w-3 mr-1" /> Vérifier checksums
            </Button>
            <Button size="sm" variant="outline" onClick={runRestoreTest} disabled={busy} data-testid="backup-restore-test-btn">
              <PlayCircle className="h-3 w-3 mr-1" /> Tester la restauration
            </Button>
          </div>
          {validateResult && (
            <div className="text-xs mt-2">
              <span className={validateResult.ok ? "text-green-700" : "text-red-700"}>
                Validation : {validateResult.ok ? "OK" : "KO"} · fichiers vérifiés : {validateResult.files_ok}
              </span>
            </div>
          )}
          {(restoreResult || latest.last_restore_test) && (() => {
            const rt = restoreResult || latest.last_restore_test;
            return (
              <div className="text-xs mt-2" data-testid="backup-restore-result">
                <div className={rt.ok ? "text-green-700" : "text-red-700"}>
                  Dernier restore-test : {rt.ok ? "OK" : "KO"} — cible temp <code>{rt.temp_db}</code> (supprimée)
                </div>
              </div>
            );
          })()}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground" data-testid="backup-none">
          Aucune sauvegarde locale pour l'instant. Cliquez « Créer une sauvegarde ».
        </p>
      )}

      {catalog.length > 0 && (
        <div className="pt-2 border-t">
          <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
            <h4 className="text-sm font-medium text-primary">Catalogue ({filteredCatalog.length} / {catalog.length})</h4>
            <div className="flex gap-1 flex-wrap items-center text-xs">
              <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value)} className="border rounded px-1 py-0.5 bg-background" data-testid="backup-sort-select">
                <option value="newest">Plus récent</option>
                <option value="oldest">Plus ancien</option>
              </select>
              <select value={filterType} onChange={(e) => setFilterType(e.target.value)} className="border rounded px-1 py-0.5 bg-background" data-testid="backup-filter-type">
                <option value="all">Tous types</option>
                <option value="manual">Manuel</option>
                <option value="scheduled">Planifié</option>
              </select>
              <select value={filterValid} onChange={(e) => setFilterValid(e.target.value)} className="border rounded px-1 py-0.5 bg-background" data-testid="backup-filter-valid">
                <option value="all">Tous statuts</option>
                <option value="valid">Restore-test OK</option>
                <option value="invalid">Sans / KO</option>
              </select>
            </div>
          </div>
          <div className="space-y-1" data-testid="backup-catalog">
            {filteredCatalog.map((c) => (
              <div key={c.checkpoint_id} className="text-xs py-1 border-b last:border-b-0" data-testid={`backup-row-${c.checkpoint_id}`}>
                <div className="flex items-center justify-between gap-2 flex-wrap">
                  <div className="flex items-center gap-2 min-w-0 flex-wrap">
                    <TypeBadge type={c.checkpoint_type} />
                    <span className="font-mono">{c.checkpoint_id}</span>
                    {c.recovery?.recovery_ready && <span className="text-emerald-700">DR</span>}
                    <RemoteBadge id={c.checkpoint_id} />
                    <span className="text-muted-foreground">· {fmtDate(c.created_at)}</span>
                  </div>
                  <div className="text-muted-foreground shrink-0">
                    {c.database?.total_documents} docs
                    {c.last_restore_test && (<> · restore-test {c.last_restore_test.ok ? "OK" : "KO"}</>)}
                  </div>
                </div>
                <div className="flex gap-1 pt-1 flex-wrap">
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => validateCheckpoint(c.checkpoint_id)} disabled={busy} data-testid={`row-validate-${c.checkpoint_id}`}>
                    <ShieldCheck className="h-3 w-3 mr-1" /> Valider
                  </Button>
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => restoreTestCheckpoint(c.checkpoint_id)} disabled={busy} data-testid={`row-restore-${c.checkpoint_id}`}>
                    <PlayCircle className="h-3 w-3 mr-1" /> Tester la restauration
                  </Button>
                  {pcloudEnabled && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-6 px-2 text-xs"
                      onClick={() => syncToPcloud(c.checkpoint_id, c)}
                      disabled={busy || syncing[c.checkpoint_id] === "in_progress"}
                      data-testid={`row-pcloud-sync-${c.checkpoint_id}`}
                    >
                      <CloudUpload className="h-3 w-3 mr-1" />
                      {syncing[c.checkpoint_id] === "in_progress" ? "Envoi…" : "Synchroniser vers pCloud"}
                    </Button>
                  )}
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => downloadCheckpoint(c.checkpoint_id)} data-testid={`row-download-${c.checkpoint_id}`}>
                    <Download className="h-3 w-3 mr-1" /> Télécharger
                  </Button>
                  <Button size="sm" variant="outline" className="h-6 px-2 text-xs" onClick={() => setExpanded((prev) => ({ ...prev, [c.checkpoint_id]: !prev[c.checkpoint_id] }))} data-testid={`row-manifest-${c.checkpoint_id}`}>
                    <FileText className="h-3 w-3 mr-1" /> Voir manifeste
                  </Button>
                </div>
                {expanded[c.checkpoint_id] && (
                  <pre className="mt-1 p-2 bg-muted/40 rounded text-[10px] overflow-x-auto max-h-64" data-testid={`row-manifest-json-${c.checkpoint_id}`}>{JSON.stringify(c, null, 2)}</pre>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="pt-2 border-t">
        <h4 className="text-sm font-medium text-primary mb-2">Providers</h4>
        <div className="space-y-2">
          {providers.map((p) => (
            <div key={p.provider} className="flex items-center justify-between text-xs" data-testid={`backup-provider-${p.provider}`}>
              <div>
                <span className="font-medium">{p.provider === "local" ? "Local" : "pCloud"}</span>
                <span className="text-muted-foreground"> — {p.message}</span>
              </div>
              <Badge className={p.enabled ? "bg-green-100 text-green-800" : "bg-slate-100 text-slate-700"}>
                {p.enabled ? "actif" : p.state === "pending_activation" ? "en attente" : "désactivé"}
              </Badge>
            </div>
          ))}
        </div>
      </div>
    </Card>
  );
}
